import { Form, Card, Badge } from 'react-bootstrap'
import { useIntl } from 'react-intl'
import { CHANNEL_CONFIG_MESSAGES as M } from '../channelConfigMessages'

// El emoji del encabezado es decoración, no texto traducible.
const SECTION_ICON = '📝'

interface PromptConfigSectionProps {
  isOrgScope: boolean
  promptHeader: string
  onPromptHeaderChange: (value: string) => void
  promptBody: string
  onPromptBodyChange: (value: string) => void
  promptFooter: string
  onPromptFooterChange: (value: string) => void
  shareContactInfo: boolean | null
  onShareContactInfoChange: (value: boolean | null) => void
  shareQuotes: boolean | null
  onShareQuotesChange: (value: boolean | null) => void
  sharePolicies: boolean | null
  onSharePoliciesChange: (value: boolean | null) => void
  shareLeads: boolean | null
  onShareLeadsChange: (value: boolean | null) => void
  shareConsentStatus: boolean | null
  onShareConsentStatusChange: (value: boolean | null) => void
}

export const PromptConfigSection = ({
  isOrgScope,
  promptHeader,
  onPromptHeaderChange,
  promptBody,
  onPromptBodyChange,
  promptFooter,
  onPromptFooterChange,
  shareContactInfo,
  onShareContactInfoChange,
  shareQuotes,
  onShareQuotesChange,
  sharePolicies,
  onSharePoliciesChange,
  shareLeads,
  onShareLeadsChange,
  shareConsentStatus,
  onShareConsentStatusChange,
}: PromptConfigSectionProps) => {
  const intl = useIntl()
  return (
    <Card className="h-100 shadow-sm border-0">
      <Card.Header className="bg-light border-0 py-3">
        <div className="d-flex align-items-center justify-content-between w-100">
          <div className="d-flex align-items-center gap-2">
            <span className="fs-3">{SECTION_ICON}</span>
            <h5 className="fw-bold text-gray-800 mb-0">{intl.formatMessage(M.promptTitle)}</h5>
          </div>
          <Badge bg="secondary">
            {intl.formatMessage(isOrgScope ? M.organization : M.promptScopeCustom)}
          </Badge>
        </div>
      </Card.Header>
      <Card.Body>
        <Form.Group className="mb-3">
          <Form.Label>
            {intl.formatMessage(M.promptHeaderLabel)}
            {!isOrgScope && (
              <span className="text-muted ms-1">{intl.formatMessage(M.promptOrgOnly)}</span>
            )}
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={promptHeader}
            onChange={(e) => onPromptHeaderChange(e.target.value)}
            disabled={!isOrgScope}
            placeholder={intl.formatMessage(M.promptHeaderPlaceholder)}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>{intl.formatMessage(M.promptBodyLabel)}</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={promptBody}
            onChange={(e) => onPromptBodyChange(e.target.value)}
            placeholder={intl.formatMessage(M.promptBodyPlaceholder)}
          />
          <Form.Text className="text-muted d-block mt-2">
            {intl.formatMessage(M.promptBodyHint)}
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>
            {intl.formatMessage(M.promptFooterLabel)}
            {!isOrgScope && (
              <span className="text-muted ms-1">{intl.formatMessage(M.promptOrgOnly)}</span>
            )}
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={promptFooter}
            onChange={(e) => onPromptFooterChange(e.target.value)}
            disabled={!isOrgScope}
            placeholder={intl.formatMessage(M.promptFooterPlaceholder)}
          />
        </Form.Group>

        <hr />

        <Form.Label className="mb-2">{intl.formatMessage(M.promptShareTitle)}</Form.Label>
        <Form.Text className="text-muted d-block mb-2">
          {intl.formatMessage(M.promptShareHint)}
        </Form.Text>

        <Form.Check
          type="checkbox"
          id="share-contact-info"
          label={intl.formatMessage(M.promptShareContact)}
          checked={!!shareContactInfo}
          onChange={(e) => onShareContactInfoChange(e.target.checked)}
          className="mb-2"
        />
        <Form.Check
          type="checkbox"
          id="share-quotes"
          label={intl.formatMessage(M.promptShareQuotes)}
          checked={!!shareQuotes}
          onChange={(e) => onShareQuotesChange(e.target.checked)}
          className="mb-2"
        />
        <Form.Check
          type="checkbox"
          id="share-policies"
          label={intl.formatMessage(M.promptSharePolicies)}
          checked={!!sharePolicies}
          onChange={(e) => onSharePoliciesChange(e.target.checked)}
          className="mb-2"
        />
        <Form.Check
          type="checkbox"
          id="share-leads"
          label={intl.formatMessage(M.promptShareLeads)}
          checked={!!shareLeads}
          onChange={(e) => onShareLeadsChange(e.target.checked)}
          className="mb-2"
        />
        <Form.Check
          type="checkbox"
          id="share-consent-status"
          label={intl.formatMessage(M.promptShareConsent)}
          checked={!!shareConsentStatus}
          onChange={(e) => onShareConsentStatusChange(e.target.checked)}
          className="mb-2"
        />
      </Card.Body>
    </Card>
  )
}
